import { CommonModule, Location } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, NavigationEnd, ActivatedRoute } from '@angular/router';
import { IonicModule, ModalController, NavController } from '@ionic/angular';
import { ColumnMode, NgxDatatableModule, SelectionType } from '@swimlane/ngx-datatable';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

import { EditCustomFieldComponent } from 'src/app/components/edit-custom-field/edit-custom-field.component';
import { EditGlobalFieldsComponent } from 'src/app/components/edit-global-fields/edit-global-fields.component';
import { EditTicketTypeComponent } from 'src/app/components/edit-ticket-type/edit-ticket-type.component';
import { TicketType } from 'src/models';
import { SharedPipesModule } from 'src/pipes/shared-pipes.module';
import { AuthService } from 'src/services/auth.service';
import { CustomFieldsService } from 'src/services/custom-fields.service';
import { MessageService } from 'src/services/message.service';
import { TicketTypesService } from 'src/services/ticket-types.service';

const TICKET_TYPES_PAGE_TABS = {
  TICKET_TYPE: 'ticketType',
  CUSTOM_FIELDS: 'customFields',
} as const;

type TicketTypesPageTab = (typeof TICKET_TYPES_PAGE_TABS)[keyof typeof TICKET_TYPES_PAGE_TABS];

@Component({
  standalone: true,
  imports: [CommonModule, FormsModule, IonicModule, NgxDatatableModule, SharedPipesModule],
  selector: 'app-ticket-types-list',
  templateUrl: './ticket-types-list.component.html',
  styleUrls: ['./ticket-types-list.component.scss'],
})
export class TicketTypesListComponent implements OnInit, OnDestroy {
  ticketTypes: Partial<TicketType>[] = [];
  filteredTicketTypes: Partial<TicketType>[] = [];

  searchText: string;
  isLoading = false;
  ColumnMode = ColumnMode;
  SelectionType = SelectionType;
  pageCount = 1;
  pageSize = 50;
  routerSub: Subscription;
  projectId: string;
  tab: TicketTypesPageTab;
  customFields: any[] = [];
  filteredCustomFields: any[] = [];
  customFieldSearchText: string = '';
  isProjectOwner = false;

  constructor(
    public authService: AuthService,
    private modalCtrl: ModalController,
    private navCtrl: NavController,
    private ticketTypesService: TicketTypesService,
    private customFieldsService: CustomFieldsService,
    private msgSrvc: MessageService,
    private router: Router,
    private route: ActivatedRoute,
    private location: Location,
  ) {
    this.projectId = this.route.snapshot.params['projectId'];
    this.isProjectOwner = this.authService.isProjectOwner(this.projectId);
  }

  async ngOnInit() {
    const currentPath = this.location.path();

    this.routerSub = this.router.events
      .pipe(filter((e) => e instanceof NavigationEnd && currentPath === e.url))
      .subscribe(async () => {
        this.isLoading = true;

        await Promise.all([this.getTicketTypes(), this.getCustomFields()]);

        this.isLoading = false;
      });

    this.route.queryParams.subscribe(async (params) => {
      // Load the ticket type modal if an id is specified in the params
      if (params.id && params.view !== TICKET_TYPES_PAGE_TABS.CUSTOM_FIELDS) {
        const modal = await this.modalCtrl.create({
          component: EditTicketTypeComponent,
          cssClass: 'modal-tall',
          componentProps: {
            id: params.id,
            projectId: this.projectId,
          },
        });

        modal.present();

        modal.onDidDismiss().then(() => {
          this.navCtrl.navigateForward(this.router.url, {
            queryParams: {},
          });
          this.getTicketTypes();
        });
      } else if (params.id && params.view === TICKET_TYPES_PAGE_TABS.CUSTOM_FIELDS) {
        const modal = await this.modalCtrl.create({
          component: EditCustomFieldComponent,
          cssClass: 'modal-tall',
          componentProps: {
            id: params.id,
            projectId: this.projectId,
          },
        });

        modal.present();

        modal.onDidDismiss().then(() => {
          this.navCtrl.navigateForward(this.router.url, {
            queryParams: {
              view: TICKET_TYPES_PAGE_TABS.CUSTOM_FIELDS,
            },
          });
          this.getCustomFields();
        });
      }

      if (params.view === TICKET_TYPES_PAGE_TABS.CUSTOM_FIELDS) {
        this.tab = TICKET_TYPES_PAGE_TABS.CUSTOM_FIELDS;
      } else {
        this.tab = TICKET_TYPES_PAGE_TABS.TICKET_TYPE;
      }
    });
  }

  ngOnDestroy() {
    this.routerSub.unsubscribe();
  }

  async getTicketTypes() {
    try {
      const ticketTypesResponse = await this.ticketTypesService.list({
        limit: null,
        order: ['name'],
        descending: false,
        where: [
          {
            field: 'projectId',
            operator: '==',
            value: this.projectId,
          },
        ],
      });

      this.ticketTypes = ticketTypesResponse.data;
      this.filteredTicketTypes = this.ticketTypes;
    } catch (err) {
      this.msgSrvc.showError(err);
    }
  }
  async getCustomFields() {
    try {
      const customFieldsResponse = await this.customFieldsService.list({
        limit: null,
        order: ['name'],
        descending: false,
        where: [{ field: 'projectId', operator: '==', value: this.projectId }],
      });

      this.customFields = customFieldsResponse.data;
      this.filteredCustomFields = this.customFields;
    } catch (err) {
      this.msgSrvc.showError(err);
    }
  }

  showNextPage() {
    this.pageCount += 1;
  }

  handleFilters() {
    this.filteredTicketTypes = this.ticketTypes;

    const term = this.searchText?.trim().toLowerCase();

    if (term) {
      this.filteredTicketTypes = this.ticketTypes.filter((ticketType) => ticketType.name.toLowerCase().includes(term));
    }
  }

  async createTicketType() {
    const modal = await this.modalCtrl.create({
      component: EditTicketTypeComponent,
      cssClass: 'modal-tall',
      componentProps: {
        projectId: this.projectId,
      },
    });

    modal.present();

    modal.onDidDismiss().then(() => {
      this.getTicketTypes();
    });
  }

  async editTicketType(id: string) {
    this.navCtrl.navigateForward(this.router.url, {
      queryParams: {
        id,
        projectId: this.projectId,
      },
    });
  }

  selectTab(selectedTab: TicketTypesPageTab) {
    this.tab = selectedTab;
    this.navCtrl.navigateForward(this.router.url.split('?')[0], {
      queryParams: {
        ...(selectedTab === TICKET_TYPES_PAGE_TABS.CUSTOM_FIELDS ? { view: TICKET_TYPES_PAGE_TABS.CUSTOM_FIELDS } : {}),
      },
    });
  }

  handleCustomFieldFilters() {
    this.filteredCustomFields = this.customFields;
    const term = this.customFieldSearchText?.trim().toLowerCase();
    if (term) {
      this.filteredCustomFields = this.customFields.filter(
        (field) =>
          field.name?.toLowerCase().includes(term) ||
          field.label?.toLowerCase().includes(term) ||
          field.description?.toLowerCase().includes(term),
      );
    }
  }

  async createCustomField() {
    const modal = await this.modalCtrl.create({
      component: EditCustomFieldComponent,
      cssClass: 'modal-small',
      componentProps: {
        projectId: this.projectId,
      },
    });

    modal.present();

    modal.onDidDismiss().then(() => {
      this.getCustomFields();
    });
  }

  async editCustomField(id: string) {
    const modal = await this.modalCtrl.create({
      component: EditCustomFieldComponent,
      componentProps: {
        id: id,
        projectId: this.projectId,
      },
      cssClass: 'modal-small',
    });
    modal.present();

    modal.onDidDismiss().then(() => {
      this.getCustomFields();
    });
  }

  getCustomFieldLabel(customField: any) {
    return customField.ticketType?.name ? `[${customField.ticketType.name}] ${customField.label}` : customField.label;
  }

  async configureGlobalCustomFields() {
    const modal = await this.modalCtrl.create({
      component: EditGlobalFieldsComponent,
      componentProps: {
        projectId: this.projectId,
      },
    });

    modal.present();
  }
}
